import { useCallback, useEffect, useRef, useState } from "react";

import { saveStoreEvaluationDraftApi } from "../../store-evaluations/storeEvaluationsApi";
import type { QuestionFieldValue } from "../components/QuestionField";

type Answers = Record<string, QuestionFieldValue>;

type Snapshot = { answers: Answers; exitPhotoAssetId: string | null };

type LocalCopy = Snapshot & { savedAt: string };

export type DraftStatus = "idle" | "saving" | "saved" | "offline";

const DEBOUNCE_MS = 1500;
const storageKey = (evaluationId: string) => `storeEvalDraft:${evaluationId}`;

const same = (a: QuestionFieldValue | undefined, b: QuestionFieldValue | undefined) =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

function readLocal(evaluationId: string): LocalCopy | null {
  try {
    const raw = window.localStorage.getItem(storageKey(evaluationId));
    return raw ? (JSON.parse(raw) as LocalCopy) : null;
  } catch {
    return null;
  }
}

function writeLocal(evaluationId: string, snapshot: Snapshot) {
  try {
    const copy: LocalCopy = { ...snapshot, savedAt: new Date().toISOString() };
    window.localStorage.setItem(storageKey(evaluationId), JSON.stringify(copy));
  } catch {
    // Sem armazenamento (aba anônima, cota): fica só o servidor.
  }
}

function removeLocal(evaluationId: string) {
  try {
    window.localStorage.removeItem(storageKey(evaluationId));
  } catch {
    // idem
  }
}

/**
 * Rascunho da auditoria de loja: grava sozinho as respostas e a foto de saída
 * na avaliação aberta (`PATCH /store-evaluations/:id/draft`), 1,5 s depois da
 * última mudança, quando a aba fica oculta, quando a rede volta e ao sair da
 * tela. Enquanto o servidor não confirma, a cópia fica no `localStorage`, para
 * a loja sem sinal; ao retomar, a cópia local mais nova que o servidor vale.
 *
 * `restore` marca o estado do servidor como base e devolve o estado para a
 * tela; `buildAnswers` monta o payload das perguntas que mudaram.
 */
export function useStoreEvaluationDraft(args: {
  token: string | null;
  answers: Answers;
  exitPhotoAssetId: string | null;
  buildAnswers: (questionIds: string[]) => any[];
}) {
  const [status, setStatus] = useState<DraftStatus>("idle");
  const [savedAt, setSavedAt] = useState<Date | null>(null);

  // Refs para os eventos da janela e o desmonte enxergarem o estado atual.
  const evaluationIdRef = useRef<string | null>(null);
  const syncedRef = useRef<Snapshot | null>(null);
  const currentRef = useRef<Snapshot>({ answers: args.answers, exitPhotoAssetId: args.exitPhotoAssetId });
  const tokenRef = useRef(args.token);
  const buildRef = useRef(args.buildAnswers);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef<Promise<void> | null>(null);

  currentRef.current = { answers: args.answers, exitPhotoAssetId: args.exitPhotoAssetId };
  tokenRef.current = args.token;
  buildRef.current = args.buildAnswers;

  const diff = useCallback(() => {
    const synced = syncedRef.current;
    const current = currentRef.current;
    if (!synced) return null;
    const ids = Object.keys(current.answers).filter((id) => !same(current.answers[id], synced.answers[id]));
    const exitChanged = current.exitPhotoAssetId !== synced.exitPhotoAssetId;
    return ids.length || exitChanged ? { ids, exitChanged, snapshot: current } : null;
  }, []);

  const flush = useCallback(
    async (opts: { keepalive?: boolean } = {}): Promise<void> => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      // Um envio por vez: quem chega durante um envio espera e manda o que sobrou.
      while (inFlightRef.current) await inFlightRef.current;

      const evaluationId = evaluationIdRef.current;
      const token = tokenRef.current;
      const pending = diff();
      if (!evaluationId || !token || !pending) return;

      const run = (async () => {
        setStatus("saving");
        try {
          const res = await saveStoreEvaluationDraftApi({
            token,
            evaluationId,
            answers: pending.ids.length ? buildRef.current(pending.ids) : undefined,
            exitPhotoAssetId: pending.exitChanged ? pending.snapshot.exitPhotoAssetId : undefined,
            keepalive: opts.keepalive,
          });
          if (evaluationIdRef.current !== evaluationId) return;
          syncedRef.current = {
            answers: { ...syncedRef.current?.answers, ...pending.snapshot.answers },
            exitPhotoAssetId: pending.snapshot.exitPhotoAssetId,
          };
          setSavedAt(res?.updatedAt ? new Date(res.updatedAt) : new Date());
          if (diff()) {
            setStatus("saving");
          } else {
            setStatus("saved");
            removeLocal(evaluationId);
          }
        } catch {
          if (evaluationIdRef.current === evaluationId) setStatus("offline");
        } finally {
          inFlightRef.current = null;
        }
      })();

      inFlightRef.current = run;
      await run;
    },
    [diff],
  );

  /** Base do servidor ao abrir ou retomar; devolve o estado da tela (com a cópia local, se mais nova). */
  const restore = useCallback(
    (evaluationId: string, server: Snapshot & { updatedAt?: string | null }): Snapshot => {
      evaluationIdRef.current = evaluationId;
      syncedRef.current = { answers: server.answers, exitPhotoAssetId: server.exitPhotoAssetId };
      setSavedAt(server.updatedAt ? new Date(server.updatedAt) : null);
      setStatus("idle");

      const local = readLocal(evaluationId);
      const serverAt = server.updatedAt ? new Date(server.updatedAt).getTime() : 0;
      if (!local || new Date(local.savedAt).getTime() <= serverAt) {
        removeLocal(evaluationId);
        return { answers: server.answers, exitPhotoAssetId: server.exitPhotoAssetId };
      }
      // Só as perguntas que ainda existem no formulário.
      const answers = { ...server.answers };
      for (const id of Object.keys(answers)) {
        if (id in local.answers) answers[id] = local.answers[id];
      }
      return { answers, exitPhotoAssetId: local.exitPhotoAssetId };
    },
    [],
  );

  /** Depois do envio: para de gravar e apaga a cópia local. */
  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (evaluationIdRef.current) removeLocal(evaluationIdRef.current);
    evaluationIdRef.current = null;
    syncedRef.current = null;
    setStatus("idle");
  }, []);

  // Cada mudança: cópia local na hora e envio depois da pausa.
  useEffect(() => {
    const evaluationId = evaluationIdRef.current;
    if (!evaluationId || !diff()) return;
    writeLocal(evaluationId, currentRef.current);
    setStatus((s) => (s === "offline" ? s : "saving"));
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void flush();
    }, DEBOUNCE_MS);
  }, [args.answers, args.exitPhotoAssetId, diff, flush]);

  useEffect(() => {
    const onHidden = () => {
      if (document.visibilityState === "hidden") void flush({ keepalive: true });
    };
    const onOnline = () => void flush();
    document.addEventListener("visibilitychange", onHidden);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onHidden);
      window.removeEventListener("online", onOnline);
      void flush({ keepalive: true });
    };
  }, [flush]);

  return { status, savedAt, restore, flush, clear };
}
